/**
 * Created by Jerker Nord on 2018-05-21.
 */

import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {ShakingDisplayObject} from "@nolimitcity/slot-game/bin/core/displayobject/ShakingDisplayObject";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {Helper} from "@nolimitcity/slot-game/bin/core/utils/Helper";
import {MathHelper} from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {Linear, TimelineLite, TweenLite} from "gsap";
import {IceIceYetiGameAssets} from "../IceIceYetiGameAssets";
import {IceIceYetiGameConfig} from "../IceIceYetiGameConfig";
import {IceIceYetiSoundConfig} from "../IceIceYetiSoundConfig";
import {IAttentionIceData} from "./IAttentionIceData";

export class IceIceYetiReelIceView extends BaseView {

    private _layer:ScalableLayer;
    private _topIcePieces:ShakingDisplayObject[][];
    private _bottomIcePieces:ShakingDisplayObject[][];
    private _topIceContainer:PIXI.Container;
    private _bottomIceContainer:PIXI.Container;
    private _iceCrackAnimations:TimelineSprite[];
    private _iceToBeRemoved:PIXI.Point[];
    private _attentionIcePieces:IAttentionIceData[];
    private _isDirty:boolean = false;
    private _playIceCrackSound:boolean = false;
    private _playIceExplodingSound:boolean = false;

    private static NO_OF_ICE_PIECES:number = 11;
    private static TOP_ICE_BASE_STRING:string = "ice/top/t00";
    private static BOTTOM_ICE_BASE_STRING:string = "ice/bottom/b00";
    private static ICE_CRACK_LEVEL_01:string = "crack/crack_00140";
    private static ICE_CRACK_LEVEL_02:string = "crack/crack_00141";
    private static ICE_EXPLOSION:string = "explosion/ice_explosion_";

    constructor() {
        super();
        this._layer = StageManager.getLayer(IceIceYetiGameConfig.instance.LAYERS.REEL_ICE.name);
        this._attentionIcePieces = [];
    }

    protected initAnimations():void {
        this._topIceContainer = new PIXI.Container();
        this.addChild(this._topIceContainer);
        this._bottomIceContainer = new PIXI.Container();
        this.addChild(this._bottomIceContainer);
        this.createTopIce();
        this.createBottomIce();
        this._iceCrackAnimations = [];
        this._layer.addChild(this);
    }

    private createTopIce():void {
        this._topIcePieces = [];
        let positionCounter:number = 0;
        let reelCounter:number = 0;
        this._topIcePieces.push([]);
        for(let i:number = 1; i < IceIceYetiReelIceView.NO_OF_ICE_PIECES; i++) {
            let iceTexture:string = IceIceYetiReelIceView.TOP_ICE_BASE_STRING.slice(0, (IceIceYetiReelIceView.TOP_ICE_BASE_STRING.length - String(i).length)) + String(i);
            let icePiece:PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures(iceTexture)[0]);
            icePiece.anchor.set(0.5, 0.5);
            if(positionCounter == 2) {
                this._topIcePieces.push([]);
                reelCounter++;
                positionCounter = 0;
            }
            this._topIcePieces[reelCounter][positionCounter] = new ShakingDisplayObject(icePiece);
            this._topIceContainer.addChild(icePiece);
            let position:[number, number] = Helper.getSymbolPositions(Math.floor((i - 1) / 2), (i - 1) % 2);
            icePiece.position.set(position[0], position[1]);
            positionCounter++;
        }
    }

    private createBottomIce():void {
        this._bottomIcePieces = [];
        let positionCounter:number = 0;
        let reelCounter:number = 0;
        this._bottomIcePieces.push([]);
        for(let i:number = 1; i < IceIceYetiReelIceView.NO_OF_ICE_PIECES; i++) {
            let iceTexture:string = IceIceYetiReelIceView.BOTTOM_ICE_BASE_STRING.slice(0, (IceIceYetiReelIceView.BOTTOM_ICE_BASE_STRING.length - String(i).length)) + String(i);
            let icePiece:PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures(iceTexture)[0]);
            icePiece.anchor.set(0.5, 0.5);
            if(positionCounter == 2) {
                this._bottomIcePieces.push([]);
                reelCounter++;
                positionCounter = 0;
            }
            this._bottomIcePieces[reelCounter][positionCounter] = new ShakingDisplayObject(icePiece);
            this._bottomIceContainer.addChild(icePiece);
            let position:[number, number] = Helper.getSymbolPositions(Math.floor((i - 1) / 2), (i - 1) % 2 + 5);
            icePiece.position.set(position[0], position[1]);
            positionCounter++;
        }
    }

    public playIceCracks(iceToBeRemoved:PIXI.Point[]):void {
        this._isDirty = true;
        this._iceToBeRemoved = iceToBeRemoved;
        this._playIceCrackSound = true;
        this._playIceExplodingSound = true;
        for(let i:number = 0; i < iceToBeRemoved.length; i++) {
            let removeReel:number = iceToBeRemoved[i].x;
            let removePosition:number = iceToBeRemoved[i].y;
            const iceCrackContainer:PIXI.Container = new PIXI.Container();
            this.addChild(iceCrackContainer);
            const position:[number, number] = Helper.getSymbolPositions(removeReel, removePosition);
            iceCrackContainer.position.set(position[0], position[1]);

            this.createIceCrackAnimation(iceCrackContainer);
        }
    }

    private createIceCrackAnimation(container:PIXI.Container):void {
        const animation:TimelineLite = new TimelineLite();
        let flipScale:PIXI.Point = this.randomizeIceCrackFlip();
        const crack01:PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures(IceIceYetiReelIceView.ICE_CRACK_LEVEL_01)[0]);
        crack01.anchor.set(0.5, 0.5);
        crack01.scale = flipScale;
        const crack02:PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures(IceIceYetiReelIceView.ICE_CRACK_LEVEL_02)[0]);
        crack02.anchor.set(0.5, 0.5);
        crack02.scale = flipScale;
        const iceExplosion:TimelineSprite = new TimelineSprite(GameResources.getTextures(IceIceYetiReelIceView.ICE_EXPLOSION));
        iceExplosion.anchor.set(0.5, 0.5);
        iceExplosion.scale = this.randomizeIceCrackFlip();

        animation.add(() => container.addChild(crack01));
        animation.add(() => this.playIceCrackSound());
        animation.add(() => container.removeChild(crack01), "+=0.3");
        animation.add(() => container.addChild(crack02));
        animation.add(() => container.removeChild(crack02), "+=0.5");
        animation.add(() => container.addChild(iceExplosion));
        animation.add(() => iceExplosion.getAnimation());
        animation.add(() => this.playIceExplodingSound());
        animation.add(() => this.removeIce());
        animation.add(() => container.removeChild(iceExplosion), "+=" + iceExplosion.totalDuration);
        animation.add(() => this.removeChild(container));
    }

    private randomizeIceCrackFlip():PIXI.Point {
        switch(MathHelper.randomIntInRange(0, 2)) {
            case 0:
                return new PIXI.Point(-1, 1);

            case 1 :
                return new PIXI.Point(1, -1);

            case 2 :
                return new PIXI.Point(1, 1);

            default :
                return new PIXI.Point(1, 1);
        }
    }

    private playIceCrackSound():void {
        if(this._playIceCrackSound == true) {
            this._playIceCrackSound = false;
            SlotGame.sound.playEffect(IceIceYetiSoundConfig.instance.ICE_CRACKING);
        }
    }

    private playIceExplodingSound():void {
        if(this._playIceExplodingSound == true) {
            this._playIceExplodingSound = false;
            SlotGame.sound.playEffect(IceIceYetiSoundConfig.instance.ICE_EXPLODING);
        }
    }

    public removeIce():void {
        for(let i:number = 0; i < this._iceToBeRemoved.length; i++) {
            let removeReel:number = this._iceToBeRemoved[i].x;
            let removePosition:number = this._iceToBeRemoved[i].y;
            const icePiece:PIXI.Sprite = <PIXI.Sprite>(removePosition < 2 ? this._topIcePieces[removeReel][removePosition].object : this._bottomIcePieces[removeReel][removePosition - 5].object);
            //this.resetIceAttention(icePiece);
            icePiece.alpha = 0;
        }
    }

    public restoreIce(iceToBeRemoved:boolean[][]):void {
        for(let i:number = 0; i < iceToBeRemoved.length; i++) {
            for(let j:number = 0; j < iceToBeRemoved[i].length; j++) {
                if(iceToBeRemoved[i][j] == true) {
                    this._isDirty = true;
                    if(j < 2) {
                        this._topIcePieces[i][j].object.alpha = 0;
                    } else if(j > 4) {
                        this._bottomIcePieces[i][j - 5].object.alpha = 0;
                    }
                }
            }
        }
    }

    public resetAllIce():void {
        if(!this._isDirty) {
            return;
        }

        const animation:TimelineLite = new TimelineLite();
        const animationArray:TweenLite[] = [];
        for(let i:number = 0; i < this._topIcePieces.length; i++) {
            for(let j:number = 0; j < this._topIcePieces[i].length; j++) {
                if(this._topIcePieces[i][j].object.alpha == 0) {
                    this._topIcePieces[i][j].object.scale.set(0, 0);
                    animationArray.push(
                        new TweenLite(this._topIcePieces[i][j].object, 0.2, {
                            alpha : 1,
                            ease : Linear.easeOut
                        }));
                    animationArray.push(
                        new TweenLite(this._topIcePieces[i][j].object.scale, 0.3, {x : 1, y : 1}));
                }
                if(this._bottomIcePieces[i][j].object.alpha == 0) {
                    this._bottomIcePieces[i][j].object.scale.set(0, 0);
                    animationArray.push(
                        new TweenLite(this._bottomIcePieces[i][j].object, 0.2, {
                            alpha : 1,
                            ease : Linear.easeOut
                        }));
                    animationArray.push(
                        new TweenLite(this._bottomIcePieces[i][j].object.scale, 0.3, {x : 1, y : 1})
                    );
                }
            }
        }
        animation.add(animationArray);
        animation.restart();

        for(let j:number = 0; j < this._iceCrackAnimations.length; j++) {
            this._iceCrackAnimations[j].destroy({children : true, texture : true, baseTexture : false});
        }

        this._isDirty = false;
    }

    public playAllIceShake():void {
        this._topIcePieces.forEach((icePieces:ShakingDisplayObject[]) => {
            icePieces.forEach((ice:ShakingDisplayObject) => {
                if(ice.object.alpha = 1) {
                    ice.shake(0.05, 3);
                }
            });
        });

        this._bottomIcePieces.forEach((icePieces:ShakingDisplayObject[]) => {
            icePieces.forEach((ice:ShakingDisplayObject) => {
                if(ice.object.alpha = 1) {
                    ice.shake(0.05, 3);
                }
            });
        });
    }

    public stopAllIceShake():void {
        this._topIcePieces.forEach((icePieces:ShakingDisplayObject[]) => {
            icePieces.forEach((ice:ShakingDisplayObject) => {
                ice.stopShake();
            });
        });

        this._bottomIcePieces.forEach((icePieces:ShakingDisplayObject[]) => {
            icePieces.forEach((ice:ShakingDisplayObject) => {
                ice.stopShake();
            });
        });
    }

    public changeMode(newMode:string):void {}

    public playIceAttention(reelId:number, symbolId:number, playSound:boolean):void {
        const attentionIce:PIXI.Sprite = <PIXI.Sprite>(symbolId < 2 ? this._topIcePieces[reelId][symbolId].object : this._bottomIcePieces[reelId][symbolId - 5].object);
        const wildExcitement:TimelineSprite = new TimelineSprite(GameResources.getTextures(IceIceYetiGameAssets.WILD_EXCITEMENT));
        wildExcitement.position = attentionIce.position;
        attentionIce.parent.addChild(wildExcitement);
        wildExcitement.anchor.set(0.5, 0.5);
        wildExcitement.show();

        TweenLite.to(attentionIce, 0.2, {alpha : 0});
        wildExcitement.getAnimation().restart();
        if(playSound) {

        }
        this._attentionIcePieces.push({attentionIce : attentionIce, wildExcitement : wildExcitement});
    }

    public resetAllIceAttention():void {
        while(this._attentionIcePieces.length > 0) {
            this.resetIceAttention(this._attentionIcePieces.pop()!);
        }
    }

    private resetIceAttention(attentionIceData:IAttentionIceData):void {
        const attentionIce:PIXI.Sprite = attentionIceData.attentionIce;
        const wildExcitement:TimelineSprite = attentionIceData.wildExcitement;
        const resetTL:TimelineLite = new TimelineLite();
        resetTL.add(
            wildExcitement.getAnimation().reverse()
        );
        resetTL.add([
            new TweenLite(attentionIce, 0.2, {
                alpha : 1,
                ease : Linear.easeOut
            }),
            new TweenLite(wildExcitement, 0.2, {
                alpha : 0,
                ease : Linear.easeOut
            })
        ], "-=0.1");
        resetTL.add(() => wildExcitement.parent.removeChild(wildExcitement));
        resetTL.add(() => wildExcitement.destroy({children : true, texture : true, baseTexture : false}));
        resetTL.restart();
    }
}